/**
 * FlowType Context Module
 *
 * @module FlowTypeContextModule
 *
 * @summary Provides context for determining the flow a user is in during onboarding for a new supergraph.
 *
 * This module defines the React context and provider for managing flow types during onboarding. It enables the application
 * to handle different flows, specifically including the GraphOS Dedicated flow, which allows users to add private subgraphs
 * via AWS Lattice networks into either a new or existing supergraph.
 *
 * The module exports:
 * - `FlowTypes`: Enumerated type values for flow types.
 * - `FlowTypeContextType`: Type definition for context value.
 * - `FlowTypeContext`: React context to provide and consume the flow type.
 * - `FlowTypeProvider`: React component to wrap child components, providing them access to the `FlowTypeContext`.
 *
 * Example usage:
 * ```jsx
 * import { FlowTypeProvider, FlowTypeContext } from './FlowTypeContextModule';
 *
 * const App = () => {
 *   return (
 *     <FlowTypeProvider>
 *       <YourComponent />
 *     </FlowTypeProvider>
 *   );
 * }
 *
 * const YourComponent = () => {
 *   const { flowType, setFlowType } = useContext(FlowTypeContext);
 *   // Your code here
 * }
 * ```
 */
import React, { createContext, useMemo, useState } from 'react';

import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';

// FlowTypes: Enumeration for types of flow.
// DEDICATED_PUBLIC: Indicates a public dedicated flow.
// DEDICATED_PRIVATE: Indicates a private dedicated flow.
// SERVERLESS: This is reserved for Serverless and other future flows.
export type FlowTypes = 'DEDICATED_PUBLIC' | 'DEDICATED_PRIVATE' | 'SERVERLESS';

// Define a type for the flow type context value
export type FlowTypeContextType = {
  flowType: FlowTypes;
  setFlowType: (value: FlowTypes) => void;
};

// Create the context with the default value matching the type
export const FlowTypeContext = createContext<FlowTypeContextType>({
  flowType: 'SERVERLESS',
  setFlowType: () => {},
});

export const FlowTypeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [accountId] = useCurrentAccountId();
  const currentPlanResult = useCurrentPlan({
    skip: !accountId,
    accountId: accountId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlanResult?.currentPlan?.tier,
    kind: currentPlanResult?.currentPlan?.kind,
  });

  // Update the useState type to match the new FlowTypes definition.
  // If the user is a dedicated customer, set the flow type to DEDICATED_PUBLIC,
  // otherwise set it to SERVERLESS.
  const [flowType, setFlowType] = useState<FlowTypes>(
    isDedicatedPlan ? 'DEDICATED_PUBLIC' : 'SERVERLESS',
  );

  const contextValue = useMemo(() => ({ flowType, setFlowType }), [flowType]);

  return (
    <FlowTypeContext.Provider value={contextValue}>
      {children}
    </FlowTypeContext.Provider>
  );
};
